import { cx } from '../cx'
import { SectionLabel } from './SectionLabel'
import styles from './StressTrack.module.css'

export interface StressBoxProps {
  /** The box's value on the track (1, 2, 3 …) — rendered inside it. */
  value: number
  filled: boolean
  onToggle?: (next: boolean) => void
  disabled?: boolean
  /** Track name, used to build an accessible label ("Physical stress 2, marked"). */
  trackName: string
}

/**
 * Spec §4 "Stress box". Open = slate-2 fill / line stroke; filled = blood fill.
 *
 * The visible square is small, but the button around it is a full 44px tap target
 * (spec §2) — the padding is the hit area, which is why the wrapper is the button
 * and not the box.
 */
export function StressBox({
  value,
  filled,
  onToggle,
  disabled = false,
  trackName,
}: StressBoxProps) {
  const label = `${trackName} stress ${value}, ${filled ? 'marked' : 'clear'}`

  return (
    <button
      type="button"
      role="switch"
      aria-checked={filled}
      aria-label={label}
      disabled={disabled || onToggle === undefined}
      onClick={() => onToggle?.(!filled)}
      className={cx(styles.hit, filled && styles.hitFilled)}
    >
      <span className={cx(styles.box, filled && styles.filled)} aria-hidden="true">
        {value}
      </span>
    </button>
  )
}

export interface StressTrackProps {
  /** e.g. "Physical", "Mental", "Hunger" — power tracks render identically. */
  name: string
  boxes: Array<{ id: number; value: number; isStressed: boolean }>
  onToggle?: (boxId: number, next: boolean) => void
  disabled?: boolean
  className?: string | undefined
}

/** A labelled row of stress boxes — one per track (spec §4, §5.1). */
export function StressTrack({ name, boxes, onToggle, disabled, className }: StressTrackProps) {
  return (
    <div className={cx(styles.track, className)}>
      <SectionLabel tone="muted" micro className={styles.trackName}>
        {name}
      </SectionLabel>
      <div className={styles.boxes} role="group" aria-label={`${name} stress track`}>
        {boxes.map((box) => (
          <StressBox
            key={box.id}
            value={box.value}
            filled={box.isStressed}
            trackName={name}
            disabled={disabled ?? false}
            {...(onToggle !== undefined && { onToggle: (next: boolean) => onToggle(box.id, next) })}
          />
        ))}
      </div>
    </div>
  )
}
